import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAICalendar } from "contexts/AICalendarContext";
import { normaliseGeneratedContent } from "utils/calendarContent";
import i18n from "i18";

/**
 * Step 5: Review Posts
 * Preview and edit generated posts before finalizing
 * @returns {JSX.Element} Step 5 Posts review editor
 */
export default function Step5Review() {
  const { state, nextStep, prevStep, setGeneratedPosts } = useAICalendar();
  
  // The generator occasionally hands back the model's JSON envelope, or an
  // object where the caption belongs. Rendering that directly is what used to
  // white-screen this step, so normalise before anything touches it.
  const [localPosts, setLocalPosts] = useState(() =>
    (state.generatedPosts || []).map((post) => ({
      ...post,
      content: { ...(post.content || {}), ...normaliseGeneratedContent(post) },
    })),
  );
  const [editingPost, setEditingPost] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editedText, setEditedText] = useState("");

  const handleEditPost = (post) => {
    setEditingPost(post);
    setEditedText(post.content.text || "");
    setEditDialogOpen(true);
  };

  const handleSaveEdit = () => {
    const updated = localPosts.map((post) =>
      post.id === editingPost.id
        ? { ...post, content: { ...post.content, text: editedText } }
        : post
    );
    setLocalPosts(updated);
    setEditDialogOpen(false);
    setEditingPost(null);
  };

  const handleDeletePost = (postId) => {
    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(i18n.t("aiCalendarWizard.step5.confirmDelete"));
    if (!confirmed) return;
    
    const updated = localPosts.filter((post) => post.id !== postId);
    setLocalPosts(updated);
  };

  const handleNext = () => {
    setGeneratedPosts(localPosts);
    nextStep();
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Typography variant="h6">
        {i18n.t("aiCalendarWizard.step5.title")}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {i18n.t("aiCalendarWizard.step5.description")}
      </Typography>

      {/* Posts Summary */}
      <Box sx={{ display: "flex", gap: 2 }}>
        <Chip
          label={`${localPosts.length} ${i18n.t("aiCalendarWizard.step5.totalPosts")}`}
          color="primary"
        />
        <Chip
          label={`${new Set(localPosts.map((p) => p.platform)).size} ${i18n.t("aiCalendarWizard.step5.platforms")}`}
          variant="outlined"
        />
      </Box>

      {/* Posts List */}
      <Grid container spacing={2}>
        {localPosts.map((post) => (
          <Grid item xs={12} md={6} key={post.id}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Chip label={post.platform} size="small" color="primary" />
                    <Typography variant="caption" color="text.secondary">
                      {post.scheduled_date} {post.scheduled_time}
                    </Typography>
                  </Box>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleEditPost(post)}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeletePost(post.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                {(post.content.unparsed || post.content.recovered) && (
                  <Chip
                    size="small"
                    color={post.content.unparsed ? "error" : "warning"}
                    variant="outlined"
                    sx={{ mb: 1 }}
                    label={i18n.t(
                      post.content.unparsed
                        ? "aiCalendarWizard.step5.contentUnreadable"
                        : "aiCalendarWizard.step5.contentRecovered",
                    )}
                  />
                )}

                <Typography variant="body2" sx={{ mb: 1, whiteSpace: "pre-wrap" }}>
                  {post.content.text}
                </Typography>

                {post.content.hashtags && post.content.hashtags.length > 0 && (
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mt: 1 }}>
                    {post.content.hashtags.map((tag, idx) => (
                      <Chip
                        key={idx}
                        label={tag}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                )}

                {post.media_suggestions && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                    📸 {post.media_suggestions.description}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{i18n.t("aiCalendarWizard.step5.editPost")}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={8}
            value={editedText}
            onChange={(e) => setEditedText(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {i18n.t("aiCalendarWizard.cancel")}
          </Button>
          <Button onClick={handleSaveEdit} variant="contained">
            {i18n.t("aiCalendarWizard.save")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={prevStep}>
          {i18n.t("aiCalendarWizard.back")}
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          disabled={localPosts.length === 0}
        >
          {i18n.t("aiCalendarWizard.next")}
        </Button>
      </Box>
    </Box>
  );
}
